import { difference, intersection, union } from 'polygon-clipping';

import type { ClipGeometry, ClipMultiPolygon, ClippingEngine } from './types';

/**
 * `polygon-clipping` 0.15.7 — a Martinez–Rueda sweep line over Shewchuk adaptive
 * predicates (`robust-predicates`).
 *
 * THIS FILE IS THE ONLY PLACE IN THE REPO PERMITTED TO IMPORT THE LIBRARY.
 *
 * Chosen for speed: measured at roughly 3–6× faster than `polyclip-ts` on the
 * `netHoleArea` hot path, which runs per cut layer per render and again in every
 * BOM recalculation. The cost is robustness — it throws "Unable to complete
 * output ring" and similar on geometry that other engines handle, which is why
 * no caller reaches it except through the guarded facade.
 *
 * If those throws turn out to bite real users, `polyclip-ts` is a drop-in with
 * byte-identical results on every case in `cutout.spec.ts` and survives all the
 * published repros; it is slower and it is what Turf 7 ships. Swap by adding an
 * adapter beside this one and changing the engine in `./index.ts`.
 *
 * The library's own `.d.ts` advertises named exports that its ESM bundle does not
 * have. Our bundlers all resolve to a build that does (esbuild takes the UMD,
 * rolldown and vitest the CJS), so the named import above is safe here — but it
 * is another reason to keep the import confined to one file.
 */
export const polygonClippingEngine: ClippingEngine = {
	name: 'polygon-clipping',

	union: (first: ClipGeometry, ...rest: ClipGeometry[]): ClipMultiPolygon =>
		union(first, ...rest),

	intersection: (first: ClipGeometry, ...rest: ClipGeometry[]): ClipMultiPolygon =>
		intersection(first, ...rest),

	difference: (subject: ClipGeometry, ...clips: ClipGeometry[]): ClipMultiPolygon =>
		difference(subject, ...clips),
};
